import type { ClaimProblemReason, ClaimProblemSummary } from '../../../types';

import type { ClaimWorkOutcomeReason } from './ReasonDetail';

/** Presentation only: preserve payer codes and never infer a denial from a code. */
export function reasonCategoryLabel(category?: string | null): string {
  const value = category?.trim();
  if (!value || /^(unknown|other|unclassified|unspecified)$/i.test(value)) {
    return 'Review payer response';
  }
  const words = value.replace(/_/g, ' ').replace(/\s+/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** The review overlay is an open JSON object in the generated contract. */
function readText(record: Record<string, unknown> | null | undefined, key: string): string | null {
  const value = record?.[key];
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function sourceReasonLabel(reason: ClaimProblemReason): string | null {
  const label = reason.title?.trim();
  return label && !/^unmapped\b/i.test(label) ? reasonCategoryLabel(label) : null;
}

export function buildReasonDetail(
  reason: ClaimWorkOutcomeReason | null | undefined,
  problem?: ClaimProblemSummary | null,
) {
  const guidance = problem?.review_guidance;
  const explanation = guidance?.problem_explanation;
  const summary =
    readText(explanation, 'hover_summary') ??
    readText(explanation, 'plain_summary') ??
    readText(explanation, 'hover_explanation') ??
    readText(guidance?.primary_finding, 'summary');
  const nextStep =
    problem?.next_action?.disabled_reason ??
    readText(explanation, 'what_to_do_next') ??
    readText(guidance?.recommended_action, 'plain_instruction') ??
    readText(guidance?.action_summary, 'instruction') ??
    problem?.next_action?.label;
  const codes = [...new Set((reason?.codes ?? []).map(code => code.trim()).filter(Boolean))];
  const sourceEntries = [problem?.reason_of_record, ...(problem?.reason_codes ?? [])].filter(
    (entry): entry is ClaimProblemReason => Boolean(entry),
  );
  const sourceReasons: { code?: string; title: string | null; description?: string | null }[] = [];
  const coveredCodes = new Set<string>();
  sourceEntries.forEach(entry => {
    const code = entry.display_code?.trim() || entry.code?.trim();
    const title = sourceReasonLabel(entry);
    const description =
      entry.short_explanation?.trim() || entry.description?.trim() || entry.definition?.trim();
    const key = (code || title || description || '').toLowerCase();
    if (!key) return;
    [entry.code, entry.display_code, entry.group_code].forEach(value => {
      if (value?.trim()) coveredCodes.add(value.trim().toLowerCase());
    });
    const existing = sourceReasons.find(
      candidate =>
        (candidate.code || candidate.title || candidate.description || '').toLowerCase() === key,
    );
    if (existing) {
      existing.title ||= title;
      existing.description ||= description === existing.title ? null : description;
    } else {
      sourceReasons.push({ code, title, description: description === title ? null : description });
    }
  });
  return {
    title:
      (problem?.reason_of_record && sourceReasonLabel(problem.reason_of_record)) ||
      reasonCategoryLabel(reason?.category),
    payerSummary: problem?.headline?.trim(),
    source: problem?.source_label?.trim(),
    summary,
    nextStep,
    nextStepIsAi:
      !problem?.next_action?.disabled_reason &&
      Boolean(
        readText(explanation, 'what_to_do_next') ||
        readText(guidance?.recommended_action, 'plain_instruction') ||
        readText(guidance?.action_summary, 'instruction') ||
        problem?.next_action?.source === 'claim_case_review',
      ),
    patientBillingNote: readText(guidance?.patient_billing_gate, 'plain_reason'),
    codes,
    sourceReasons,
    additionalCodes: codes.filter(code => !coveredCodes.has(code.toLowerCase())),
  };
}
